import { lazy, StrictMode, Suspense } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from 'react-router';
import { AuthProvider } from './contexts/AuthContext';
import EvalSetListPage from './EvalSetListPage.tsx';
import OAuthCallback from './pages/OAuthCallback.tsx';
import SamplesPage from './SamplesPage.tsx';
import ScansPage from './ScansPage.tsx';
import TranscriptSearchPage from './TranscriptSearchPage.tsx';
import SamplePermalink from './routes/SamplePermalink.tsx';
import LaunchPage from './pages/LaunchPage.tsx';
import JobsPage from './JobsPage.tsx';
import EvalSetStatusPage from './pages/EvalSetStatusPage.tsx';
import EvalSetApprovalsPage from './pages/EvalSetApprovalsPage.tsx';
import JobConfigPage from './pages/JobConfigPage.tsx';
import SettingsPage from './pages/SettingsPage.tsx';
import { AppQueryProvider } from './QueryProvider.tsx';
import { LoadingDisplay } from './components/LoadingDisplay.tsx';
import { BootShell } from './components/BootShell.tsx';
import { RouteErrorBoundary } from './components/RouteErrorBoundary.tsx';
import { Layout } from './components/Layout.tsx';

// Each viewer ships a global stylesheet (Bootstrap plus its own theme
// overrides) that cascades across the whole document. Importing both eagerly
// puts both stylesheets in <head> at once, so each viewer's globals leak
// into the other's pages. Splitting them via React.lazy — combined with the
// full page reloads our cross-viewer links already use — keeps only the
// active viewer's stylesheet loaded.
const ArtifactPage = lazy(() => import('./pages/ArtifactPage.tsx'));
const ReportPage = lazy(() => import('./pages/ReportPage.tsx'));
const SampleTimelinePage = lazy(() => import('./pages/SampleTimelinePage.tsx'));
const EvalSetTimelinePage = lazy(
  () => import('./pages/EvalSetTimelinePage.tsx')
);
const EvalPage = lazy(() => import('./EvalPage.tsx'));
const ScanPage = lazy(() => import('./ScanPage.tsx'));
// Lazy so Recharts (used only by the historic view) stays out of the main bundle.
const RateLimitsPage = lazy(() => import('./RateLimitsPage.tsx'));

const FallbackRoute = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const logDir = searchParams.get('log_dir');

  if (logDir) {
    // Handle old URL format with log_dir param
    return (
      <Navigate
        replace
        to={{
          pathname: `/eval-set/${encodeURIComponent(logDir)}`,
          hash: location.hash,
        }}
      />
    );
  }

  // Default to jobs list
  return <Navigate replace to="/jobs" />;
};

/** Route content separated from browser history so transitions can be tested. */
export function HawkRoutes() {
  return (
    <Routes>
      <Route path="oauth/complete" element={<OAuthCallback />} />
      <Route
        path="*"
        element={
          <AuthProvider>
            {/* A page that throws (or a route chunk that fails to load) shows
                an error in the frame instead of unmounting the whole app. */}
            <RouteErrorBoundary>
              <Suspense
                fallback={
                  <BootShell>
                    <LoadingDisplay />
                  </BootShell>
                }
              >
                <Routes>
                  {/* Embedded viewers own their chrome and global styles. */}
                  <Route path="scan/:scanFolder/*" element={<ScanPage />} />
                  <Route path="eval-set/:evalSetId/*" element={<EvalPage />} />
                  <Route
                    path="eval-set/:evalSetId/sample/:sampleUuid/artifacts/*"
                    element={<ArtifactPage />}
                  />
                  <Route
                    path="eval-set/:evalSetId/sample/:sampleUuid/reports/*"
                    element={<ReportPage />}
                  />
                  <Route
                    path="permalink/sample/:uuid"
                    element={<SamplePermalink />}
                  />
                  {/* One persistent shell; lazy pages suspend only inside its pane. */}
                  <Route element={<Layout />}>
                    <Route
                      path="eval-set/:evalSetId/sample/:sampleUuid/timeline"
                      element={<SampleTimelinePage />}
                    />
                    <Route
                      path="eval-set/:evalSetId/status"
                      element={<EvalSetStatusPage />}
                    />
                    <Route
                      path="eval-set/:evalSetId/approvals"
                      element={<EvalSetApprovalsPage />}
                    />
                    <Route
                      path="eval-set/:evalSetId/timeline"
                      element={<EvalSetTimelinePage />}
                    />
                    <Route
                      path="eval-set/:jobId/config"
                      element={<JobConfigPage jobType="eval-set" />}
                    />
                    <Route
                      path="scan/:jobId/config"
                      element={<JobConfigPage jobType="scan" />}
                    />
                    <Route path="eval-sets" element={<EvalSetListPage />} />
                    <Route path="samples" element={<SamplesPage />} />
                    <Route path="scans" element={<ScansPage />} />
                    <Route path="usage" element={<RateLimitsPage />} />
                    <Route path="search" element={<TranscriptSearchPage />} />
                    <Route path="jobs" element={<JobsPage />} />
                    <Route path="launch" element={<LaunchPage />} />
                    <Route path="settings/:tab?" element={<SettingsPage />} />
                    <Route path="*" element={<FallbackRoute />} />
                  </Route>
                </Routes>
              </Suspense>
            </RouteErrorBoundary>
          </AuthProvider>
        }
      />
    </Routes>
  );
}

export const AppRouter = () => (
  <StrictMode>
    <AppQueryProvider>
      <BrowserRouter>
        <HawkRoutes />
      </BrowserRouter>
    </AppQueryProvider>
  </StrictMode>
);
